// OwnLimitationForm.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Stack,
  Button,
} from '@mui/material';
import { Formik, Form, Field, type FormikProps, type FieldProps } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from '../../../tools/error';
// import { Subtenant, SubtenantQuota } from '../../../api/subtenantApi';
import { Entity } from '../../../types/entity';
import { LocalizedDateTimePicker } from '../../localizedDatePicker/LocalizedDateTimePicker';
import dayjs from 'dayjs';
import { CustomerQuota } from '../../../api/entityApi';

const initialValues: CustomerQuota = {
    customer_id: 0,
    entity_id: 0,
    name: '',
    max_parking_slots: 0,
    max_permits: 0,
    occupied_parking_slots: 0,
}

export interface OwnLimitationFormProps {
  quota: CustomerQuota | null;
  onSave: (values: CustomerQuota) => void;
  onCancel: () => void;
  entities: Entity[];
}

const OwnLimitationForm: React.FC<OwnLimitationFormProps> = ({
  quota,
  onSave,
  onCancel,
  entities,
}) => {
    const { t } = useTranslation();
    const [submitError, setSubmitError] = useState<string | null>(null);

    const validationSchema = Yup.object({
      max_parking_slots: Yup.number().required(('max_parking_slots_is_required')),
      max_permits: Yup.number().required(('max_permits_is_required')),
      occupied_parking_slots: Yup.number().required(('occupied_parking_slots_is_required')),
      ts_update: Yup.date(),
    });
  
    return (
    <Formik
        initialValues={quota || initialValues}
        validationSchema={validationSchema}
        // enableReinitialize={true}
        validateOnMount={true}
      onSubmit={ async (values, { setSubmitting, setStatus }) => {
        setSubmitError(null);
        try {
          await onSave(values);
        } catch (error) {
          console.error("Save failed:", error);
          const errorMessage = getErrorMessage(
            error, 
            t('PlateForm.Failed_to_save_plate_general') // Сообщение по умолчанию
          );
          setSubmitError(errorMessage);
          setSubmitting(false);        
        }
      }}
    >
      {(formikProps: FormikProps<CustomerQuota>) => {
        const { values, setFieldValue } = formikProps;
        return (
          <Form>
            <Typography variant='h4'>
                Власне Обмеження
            </Typography>
            <br/>
            <Typography variant='h5'>
                локація: {entities.find(e => e.entity_id == quota?.entity_id)?.name}
            </Typography>
            <br/>
            {/* <Typography variant='h6'>
                орендар: {subtenants.find(s => s.subtenant_id == quota?.subtenant_id)?.name}
            </Typography>
            <br/> */}

            <Stack spacing={2} sx={{ mt: 2 }}>

              {submitError && (
                <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                  {submitError}
                </Typography>
              )}

              <Field name="max_parking_slots">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    // label={t('Quota.max_parking_slots')}
                    label={'max паркомісць'}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>
              
              <Field name="max_permits">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    // label={t('Quota.max_permits')}
                    label={'max перепусток'}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>

              <Field name="occupied_parking_slots">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    // label={t('Quota.occupied_parking_slots')}
                    label={'зайнято паркомісць'}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>

            <Field name="ts_update">
                {({ field, meta }: any) => (
                <LocalizedDateTimePicker
                    {...field}
                    label={t('LimitationForm.ts_update')}
                    // format="DD.MM.YYYY HH:mm" 
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(newValue) => {
                    setFieldValue("ts_update", newValue ? newValue.toISOString() : '');
                    }}
                    slotProps={{
                    textField: {
                        fullWidth: true,
                        error: meta.touched && !!meta.error,
                        helperText: meta.touched && meta.error,
                    },
                    }}
                />
                )}
            </Field>
              

            </Stack>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button sx={{ mr: 1 }} disabled={formikProps.isSubmitting} variant='outlined' onClick={ async ()=> {
                  onCancel();
                }}>
                  {t('OriginForm.Cancel')}
                </Button>

                <Button type="submit" variant="contained" disabled={formikProps.isSubmitting || !formikProps.isValid}>
                  {formikProps.isSubmitting ? t('OriginForm.Saving') : t('OriginForm.Save')}
                </Button>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>

  );
}

export default OwnLimitationForm;